import { useState, useRef, useEffect } from "react";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, hasPerm, fmtDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, HandCoins, CheckCircle2, Printer, Loader2, User, ReceiptText, Bus, X, BadgePercent } from "lucide-react";
import { toast } from "sonner";
import ReceiptActions from "./ReceiptActions";
import CollectOldDuesDialog from "./CollectOldDuesDialog";

function nowLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toOffsetIso(local) {
  const d = new Date(local);
  const pad = (n) => String(n).padStart(2, "0");
  const off = -d.getTimezoneOffset();
  const sign = off >= 0 ? "+" : "-";
  const oh = pad(Math.floor(Math.abs(off) / 60)), om = pad(Math.abs(off) % 60);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${oh}:${om}`;
}

function sessionMonths(session) {
  const names = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
  let start = new Date().getFullYear();
  if (session && /^\d{4}/.test(session)) start = parseInt(session.slice(0, 4), 10);
  return names.map((mm, i) => `${mm} ${i < 9 ? start : start + 1}`);
}

export default function CollectFeeDialog({ open, onOpenChange, studentId = null, onCollected }) {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);
  const [searching, setSearching] = useState(false);
  const [headMonths, setHeadMonths] = useState({});
  const [selectedHeads, setSelectedHeads] = useState(new Set());
  const [receiptNo, setReceiptNo] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [allowEditDate, setAllowEditDate] = useState(true);
  const [selectedTMonths, setSelectedTMonths] = useState([]);
  const [assignTOpen, setAssignTOpen] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [tRoute, setTRoute] = useState("");
  const [tVehicle, setTVehicle] = useState("");
  const [tPickup, setTPickup] = useState("");
  const [tFee, setTFee] = useState(0);
  const [tAssignMonths, setTAssignMonths] = useState([]);
  const [amount, setAmount] = useState("");
  const [discount, setDiscount] = useState("");
  const [mode, setMode] = useState("Cash");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [history, setHistory] = useState([]);
  const [oldDuesOpen, setOldDuesOpen] = useState(false);
  const [allowDiscount, setAllowDiscount] = useState(true);
  const [discountHeads, setDiscountHeads] = useState([]);
  const [discountHead, setDiscountHead] = useState("");
  const amtRef = useRef();

  const canDiscount = hasPerm(user, "add_discount");
  const token = localStorage.getItem("token");

  const loadHistory = async (sid) => {
    try {
      const r = await api.get("/payments", { params: { student_id: sid } });
      setHistory(r.data);
    } catch (e) { setHistory([]); }
  };

  const loadPreview = async (sid) => {
    try {
      const r = await api.get(`/fees/preview/${sid}`);
      setSel(r.data);
      setResults([]);
      setReceipt(null);
      setSelectedHeads(new Set());
      setHeadMonths({});
      setSelectedTMonths([]);
      setAmount("0");
      setPaymentDate(nowLocal());
      setDiscount("");
      setDiscountHead("");
      loadHistory(sid);
      api.get("/fees/next-receipt").then((rr) => setReceiptNo(rr.data.receipt_number)).catch(() => setReceiptNo(""));
      setTimeout(() => amtRef.current?.focus(), 150);
    } catch (e) { toast.error("Failed to load student fees"); }
  };

  useEffect(() => {
    if (open) {
      setQ(""); setResults([]); setReceipt(null); setSel(null); setHistory([]);
      setDiscount(""); setRemarks(""); setMode("Cash"); setHeadMonths({}); setAmount(""); setDiscountHead(""); setPaymentDate(nowLocal());
      api.get("/settings").then((r) => { setAllowDiscount(r.data?.allow_discount !== false); setDiscountHeads((r.data?.discount_heads || []).map((h) => (typeof h === "string" ? h : h.name))); setAllowEditDate(r.data?.allow_edit_collect_date !== false); }).catch(() => {});
      if (studentId) loadPreview(studentId);
    }
    // eslint-disable-next-line
  }, [open, studentId]);

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    try {
      const r = await api.get("/students", { params: { search: q, limit: 8 } });
      setResults(r.data.students);
      if (r.data.students.length === 1) loadPreview(r.data.students[0].id);
    } finally { setSearching(false); }
  };

  const [manualMode, setManualMode] = useState(false);
  const [manualAmount, setManualAmount] = useState("");
  const [oldDuesAlloc, setOldDuesAlloc] = useState([]);

  const isMulti = (b) => b.fee_name !== "Transport Fee" && (((b.collection_months || []).length || 0) > 1 || b.frequency === "Monthly");
  const monthsForHead = (b) => {
    const sm = sessionMonths(sel.student.academic_session);
    const cm = b.collection_months || [];
    return cm.length ? cm.map((i) => sm[i]).filter(Boolean) : sm;
  };

  const recalc = (heads, hMonths, tMonths) => {
    if (!sel) return;
    const monthly = sel.fees.breakdown
      .filter((b) => isMulti(b) && heads.has(b.fee_name))
      .reduce((a, b) => a + (b.unit || 0) * ((hMonths[b.fee_name] || []).length), 0);
    const tHead = sel.fees.breakdown.find((b) => b.fee_name === "Transport Fee");
    const tUnit = tHead && heads.has("Transport Fee") ? (tHead.unit || 0) : 0;
    const nmt = sel.fees.breakdown.filter((b) => !isMulti(b) && b.fee_name !== "Transport Fee" && heads.has(b.fee_name)).reduce((a, b) => a + (b.amount || 0), 0);
    setAmount(String(Math.min(Math.round(monthly + tMonths.length * tUnit + nmt), sel.fees.due)));
  };

  const distributeManual = (T) => {
    if (!sel) return;
    let rem = Number(T) || 0;
    const sm = sessionMonths(sel.student.academic_session);
    const oldAlloc = [];
    for (const od of (sel.fees.old_dues || [])) {
      if (rem <= 0.009) break;
      const avail = od.remaining != null ? od.remaining : od.amount;
      const take = Math.min(rem, avail);
      if (take > 0.009) { oldAlloc.push({ id: od.id, label: od.label, amount: Math.round(take) }); rem -= take; }
    }
    const hm = {}; const heads = new Set(); const tMonths = [];
    const tHead = sel.fees.breakdown.find((b) => b.fee_name === "Transport Fee");
    rem = Math.min(rem, sel.fees.due);
    for (let i = 0; i < 12; i++) {
      for (const b of sel.fees.breakdown) {
        if (b.fee_name === "Transport Fee") continue;
        if ((b.discount || 0) > 0) continue;
        const multi = isMulti(b);
        const cms = (b.collection_months && b.collection_months.length) ? b.collection_months : (multi ? Array.from({ length: 12 }, (_, k) => k) : [0]);
        if (!cms.includes(i)) continue;
        const label = sm[i];
        if (multi) {
          const paidM = new Set((sel.fees.head_paid_months || {})[b.fee_name] || []);
          if (paidM.has(label) || (hm[b.fee_name] || []).includes(label)) continue;
          const unit = b.unit || 0;
          if (unit <= 0 || rem + 0.001 < unit) continue;
          hm[b.fee_name] = [...(hm[b.fee_name] || []), label]; heads.add(b.fee_name); rem -= unit;
        } else {
          if (heads.has(b.fee_name)) continue;
          const paidForHead = (sel.fees.head_paid || {})[b.fee_name] || 0;
          const amt = b.amount || 0;
          if (amt <= 0 || paidForHead >= amt - 0.01) continue;
          const remainingHead = amt - paidForHead;
          if (rem + 0.001 < remainingHead) continue;
          heads.add(b.fee_name); rem -= remainingHead;
        }
      }
      if (tHead && (tHead.discount || 0) <= 0 && sel.transport?.assigned) {
        const label = sm[i];
        const allowed = !sel.transport.months.length || sel.transport.months.includes(label);
        if (allowed && !paidTMonths.has(label) && !tMonths.includes(label)) {
          const unit = tHead.unit || 0;
          if (unit > 0 && rem + 0.001 >= unit) { tMonths.push(label); heads.add("Transport Fee"); rem -= unit; }
        }
      }
    }
    setOldDuesAlloc(oldAlloc);
    setSelectedHeads(heads);
    setHeadMonths(hm);
    setSelectedTMonths(tMonths);
    recalc(heads, hm, tMonths);
  };

  const toggleHead = (name) => {
    const bk = sel?.fees?.breakdown?.find((x) => x.fee_name === name);
    if (bk && (bk.discount || 0) > 0) return;
    const next = new Set(selectedHeads);
    const nextMonths = { ...headMonths };
    if (next.has(name)) { next.delete(name); delete nextMonths[name]; }
    else next.add(name);
    setSelectedHeads(next);
    setHeadMonths(nextMonths);
    recalc(next, nextMonths, selectedTMonths);
  };

  const toggleHeadMonth = (head, m) => {
    const paidM = new Set((sel?.fees?.head_paid_months || {})[head] || []);
    if (paidM.has(m)) return;
    const cur = headMonths[head] || [];
    const nextArr = cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m];
    const next = { ...headMonths, [head]: nextArr };
    setHeadMonths(next);
    recalc(selectedHeads, next, selectedTMonths);
  };

  const toggleTMonth = (m) => {
    if (transportDiscounted) return;
    if (paidTMonths.has(m)) return;
    if (sel?.transport?.months?.length && !sel.transport.months.includes(m)) return;
    const isSelected = selectedTMonths.includes(m);
    if (!isSelected && !selectedHeads.has("Transport Fee")) { toast.error("Select Fee Head first"); return; }
    const next = isSelected ? selectedTMonths.filter((x) => x !== m) : [...selectedTMonths, m];
    setSelectedTMonths(next);
    recalc(selectedHeads, headMonths, next);
  };

  const openAssignT = async () => {
    setTRoute(""); setTVehicle(""); setTPickup(""); setTFee(0); setTAssignMonths([]);
    try { const [r, v] = await Promise.all([api.get("/routes"), api.get("/vehicles")]); setRoutes(r.data); setVehicles(v.data); } catch { setRoutes([]); setVehicles([]); }
    setAssignTOpen(true);
  };
  const saveAssignT = async () => {
    if (!tRoute) { toast.error("Select a route"); return; }
    try {
      await api.post("/transport/assign", { student_id: sel.student.id, route_id: tRoute, pickup_point: tPickup, vehicle_id: tVehicle, transport_fee: Number(tFee || 0), transport_months: tAssignMonths });
      toast.success("Transport assigned");
      setAssignTOpen(false);
      loadPreview(sel.student.id);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const selectFullDue = () => {
    if (!sel) return;
    const sm = sessionMonths(sel.student.academic_session);
    const hm = {}; const heads = new Set(); const tMonths = [];
    const tHead = sel.fees.breakdown.find((b) => b.fee_name === "Transport Fee");
    for (const b of sel.fees.breakdown) {
      if (b.fee_name === "Transport Fee") continue;
      if ((b.discount || 0) > 0) continue; // never auto-collect a discounted head
      const multi = isMulti(b);
      const cms = (b.collection_months && b.collection_months.length) ? b.collection_months : (multi ? Array.from({ length: 12 }, (_, k) => k) : [0]);
      if (multi) {
        const paidM = new Set((sel.fees.head_paid_months || {})[b.fee_name] || []);
        const months = cms.map((i) => sm[i]).filter(Boolean).filter((label) => !paidM.has(label));
        if (months.length && (b.unit || 0) > 0) { hm[b.fee_name] = months; heads.add(b.fee_name); }
      } else {
        const paidForHead = (sel.fees.head_paid || {})[b.fee_name] || 0;
        const amt = b.amount || 0;
        if (amt > 0 && paidForHead < amt - 0.01) heads.add(b.fee_name);
      }
    }
    if (tHead && (tHead.discount || 0) <= 0 && sel.transport?.assigned) {
      for (let i = 0; i < 12; i++) {
        const label = sm[i];
        const allowed = !sel.transport.months.length || sel.transport.months.includes(label);
        if (allowed && !paidTMonths.has(label)) tMonths.push(label);
      }
      if (tMonths.length) heads.add("Transport Fee");
    }
    setOldDuesAlloc([]);
    setSelectedHeads(heads);
    setHeadMonths(hm);
    setSelectedTMonths(tMonths);
    recalc(heads, hm, tMonths);
  };

  const save = async () => {
    const amt = Number(amount) || 0;
    if (manualMode) {
      if (amt <= 0 && oldDuesAlloc.length === 0) { toast.error("Enter an amount to distribute"); return; }
    } else if (!amount || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!manualMode && amt > 0 && selectedHeads.size === 0) { toast.error("Select at least one fee head to collect"); return; }
    if (amt > sel.fees.due + 0.01) { toast.error("Amount cannot exceed the total due"); return; }
    setSaving(true);
    try {
      let lastReceipt = null;
      if (manualMode && oldDuesAlloc.length) {
        for (const od of oldDuesAlloc) {
          const rr = await api.post("/fees/collect-old-dues", { student_id: sel.student.id, carried_id: od.id, amount_paid: od.amount, discount: 0, payment_mode: mode, remarks });
          lastReceipt = rr.data;
        }
      }
      if (amt > 0) {
        const order = sessionMonths(sel.student.academic_session);
        const allMonths = [...new Set(Object.values(headMonths).flat())].sort((a, b) => order.indexOf(a) - order.indexOf(b));
        const r = await api.post("/fees/collect", {
          student_id: sel.student.id, fee_month: allMonths.join(", "), current_fee: sel.fees.total_fee,
          transport_fee: sel.fees.transport_fee, discount: 0,
          amount_paid: amt, payment_mode: mode, remarks, receipt_number: receiptNo,
          heads: [...selectedHeads], transport_months: selectedTMonths, discount_head: "",
          head_months: headMonths,
          payment_date: paymentDate ? toOffsetIso(paymentDate) : undefined,
        });
        lastReceipt = r.data;
      }
      setReceipt(lastReceipt);
      toast.success(`Receipt ${lastReceipt?.receipt_number || ""} generated`);
      loadHistory(sel.student.id);
      onCollected?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reset = () => { setSel(null); setQ(""); setResults([]); setReceipt(null); setHistory([]); };
  const remaining = sel ? sel.fees.due - Number(amount || 0) : 0;
  const transportDiscounted = sel ? (((sel.fees.breakdown.find((b) => b.fee_name === "Transport Fee") || {}).discount || 0) > 0) : false;
  const paidTMonths = new Set(
    history.filter((p) => p.status !== "Void")
      .flatMap((p) => (p.transport_months_paid || []).map((s) => (s || "").trim()).filter(Boolean))
  );

  if (!open) return null;
  return (
    <>
    <div data-testid="collect-fee-page">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-5 bg-white rounded-xl border border-slate-200 px-5 py-3">
          <h2 className="font-heading text-2xl font-extrabold text-slate-900">Collect Fees</h2>
          <Button variant="ghost" size="icon" className="rounded-full h-10 w-10" onClick={() => onOpenChange(false)} data-testid="close-collect-btn" title="Close"><X size={22} /></Button>
        </div>

        {!sel && !studentId && (
          <div className="pt-2">
            <form onSubmit={doSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input className="pl-11 h-12" autoFocus placeholder="Student name, admission no or phone…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="collect-search-input" />
              </div>
              <Button type="submit" className="h-12 px-6" disabled={searching} data-testid="collect-search-btn">
                {searching ? <Loader2 className="animate-spin" /> : "Search"}
              </Button>
            </form>
            {results.length > 0 && (
              <div className="mt-4 divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                {results.map((s) => (
                  <button key={s.id} onClick={() => loadPreview(s.id)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-indigo-50 text-left" data-testid={`result-${s.admission_number}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><User size={16} /></div>
                      <div><div className="font-medium text-slate-800">{s.name}</div><div className="text-xs text-slate-400">{s.admission_number} · {s.class_name} · {s.father_name}</div></div>
                    </div>
                    <span className={`font-semibold tabular ${s.fees_summary.due > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(s.fees_summary.due)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {sel && !receipt && (
          <div className="flex flex-col gap-5 pt-1">
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 order-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold text-slate-900">Student & Dues</h3>
              </div>
              <div className="mb-4">
                <div className="text-lg font-semibold text-slate-900">{sel.student.name}</div>
                <div className="text-sm text-slate-500">{sel.student.admission_number} · {sel.student.class_name} {sel.student.section} · {sel.student.father_name}</div>
              </div>
              <div className="space-y-2 text-sm">
                {sel.fees.breakdown.map((b, i) => (
                  <div key={i} className="flex justify-between text-slate-600"><span>{b.fee_name} <span className="text-xs text-slate-400">({b.frequency})</span></span><span className="tabular">{fmt(b.amount)}</span></div>
                ))}
                <div className="border-t border-slate-200 pt-2 flex justify-between font-medium"><span>Total Payable</span><span className="tabular">{fmt(sel.fees.total_payable)}</span></div>
                <div className="flex justify-between text-emerald-600"><span>Paid</span><span className="tabular">{fmt(sel.fees.paid)}</span></div>
                <div className="flex justify-between text-amber-600"><span>Discount</span><span className="tabular">{fmt(sel.fees.discount)}</span></div>
                <div className="flex justify-between text-lg font-bold text-rose-600 border-t border-slate-200 pt-2"><span>Due</span><span className="tabular">{fmt(sel.fees.due)}</span></div>
              </div>

              {(sel.fees.old_dues || []).some((o) => o.remaining > 0) && (
                <div className="mt-4 pt-3 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-1.5">
                    <h4 className="font-semibold text-slate-800 text-sm">Old Dues (previous session)</h4>
                    <Button size="sm" variant="outline" className="h-7 text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => setOldDuesOpen(true)} data-testid="open-old-dues-collect">Collect Old Dues</Button>
                  </div>
                  <div className="space-y-1 text-sm">
                    {sel.fees.old_dues.filter((o) => o.remaining > 0).map((o) => (
                      <div key={o.id} className="flex justify-between text-amber-700" data-testid={`old-due-line-${o.id}`}>
                        <span>{o.label} <span className="text-xs text-slate-400">(from {o.from_session || "—"})</span></span>
                        <span className="tabular font-semibold">{fmt(o.remaining)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-slate-200">
                <h4 className="font-semibold text-slate-800 text-sm mb-2 flex items-center gap-1.5"><ReceiptText size={15} />Payment History</h4>
                {history.length === 0 ? (
                  <div className="text-xs text-slate-400 py-2" data-testid="history-empty">No previous payments.</div>
                ) : (
                  <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1" data-testid="payment-history">
                    {history.map((p) => (
                      <div key={p.id} className={`flex items-center justify-between text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 ${p.status === "Void" ? "opacity-50" : ""}`} data-testid={`history-${p.receipt_number}`}>
                        <div>
                          <div className="font-mono font-medium text-slate-700">{p.receipt_number}{p.is_old_dues && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-sans font-semibold">Old Dues</span>}{p.status === "Void" && <span className="ml-1 text-rose-500">(Void)</span>}</div>
                          <div className="text-slate-400">{fmtDate(p.payment_date)} · {p.payment_mode}{p.fee_month ? ` · ${p.fee_month}` : ""}</div>
                          {p.edited_by && <div className="text-amber-600" data-testid={`history-edited-${p.receipt_number}`}>edited by {p.edited_by}{p.edited_at ? ` · ${p.edited_at.slice(0, 10)}` : ""}</div>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold tabular text-emerald-600">{fmt(p.amount_paid)}</span>
                          <a href={`${API}/receipts/${p.id}/pdf?token=${token}`} target="_blank" rel="noreferrer" title="Print / Download" data-testid={`history-print-${p.receipt_number}`}>
                            <Printer size={14} className="text-slate-400 hover:text-indigo-600" />
                          </a>
                          <ReceiptActions payment={p} onChanged={() => loadPreview(sel.student.id)} iconOnly />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="order-1 bg-white rounded-xl border border-slate-200 p-5" data-testid="payment-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-slate-900">Payment</h3>
                {!studentId && <button onClick={reset} className="text-sm text-indigo-600 hover:underline" data-testid="change-student-btn">Change Student</button>}
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Fee Heads to Collect</Label>
                    <div className="mt-1 border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="fee-heads-list">
                      {sel.fees.breakdown.map((b) => {
                        const on = selectedHeads.has(b.fee_name);
                        const monthly = b.frequency === "Monthly";
                        const paidForHead = (sel.fees.head_paid || {})[b.fee_name] || 0;
                        const fullyPaid = b.amount > 0 && paidForHead >= b.amount - 0.01;
                        const discounted = (b.discount || 0) > 0;
                        const locked = discounted || fullyPaid;
                        return (
                          <label key={b.fee_name} className={`relative flex items-center justify-between px-3 py-2 ${discounted ? "cursor-not-allowed bg-amber-50/60" : fullyPaid ? "cursor-not-allowed bg-emerald-50/60" : "cursor-pointer hover:bg-slate-50"}`} data-testid={`head-${b.fee_name}`}>
                            <span className={`flex items-center gap-2 text-sm ${locked ? "text-slate-400" : "text-slate-700"}`}>
                              <input type="checkbox" checked={locked ? false : on} disabled={locked} onChange={() => toggleHead(b.fee_name)} className="accent-indigo-600 w-4 h-4 disabled:opacity-40" data-testid={`head-cb-${b.fee_name}`} />
                              {b.fee_name} <span className="text-xs text-slate-400">({b.frequency})</span>
                              {monthly && !discounted && <span className="ml-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[10px] font-semibold" data-testid={`head-recurring-${b.fee_name}`}>Recurring</span>}
                            </span>
                            {discounted
                              ? <span className="absolute left-1/2 -translate-x-1/2 text-xs font-semibold text-amber-600 flex items-center gap-1" data-testid={`head-discounted-${b.fee_name}`}><BadgePercent size={13} />Fees Discounted</span>
                              : fullyPaid && <span className="absolute left-1/2 -translate-x-1/2 text-xs font-semibold text-emerald-600 flex items-center gap-1" data-testid={`head-paid-${b.fee_name}`}><CheckCircle2 size={13} />Paid</span>}
                            <span className="text-sm tabular text-slate-600">{monthly ? `${fmt(b.unit)}/mo` : fmt(b.amount)}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  {sel.fees.breakdown.filter((b) => isMulti(b) && selectedHeads.has(b.fee_name)).map((b) => {
                    const selM = headMonths[b.fee_name] || [];
                    const paidM = new Set((sel.fees.head_paid_months || {})[b.fee_name] || []);
                    return (
                      <div key={b.fee_name} data-testid={`head-month-section-${b.fee_name}`}>
                        <Label>{b.fee_name} — Month(s) <span className="text-xs text-slate-400">({fmt(b.unit)}/mo)</span></Label>
                        <div className="grid grid-cols-4 gap-1.5 mt-1" data-testid={`head-month-list-${b.fee_name}`}>
                          {monthsForHead(b).map((m) => {
                            const on = selM.includes(m);
                            const paid = paidM.has(m);
                            return (
                              <button key={m} type="button" disabled={paid} onClick={() => toggleHeadMonth(b.fee_name, m)} title={paid ? "Already paid" : ""} className={`px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${paid ? "bg-emerald-50 text-emerald-600 border-emerald-200 cursor-not-allowed line-through" : on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`} data-testid={`head-month-${b.fee_name}-${m.replace(" ", "-")}`}>{m}{paid ? " ✓" : ""}</button>
                            );
                          })}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">Months with ✓ are already paid.</p>
                      </div>
                    );
                  })}
                  {![...selectedHeads].some((h) => sel.fees.breakdown.some((b) => b.fee_name === h && isMulti(b))) && (
                    <p className="text-xs text-slate-400" data-testid="no-monthly-head-hint">Select a fee head with multiple collection months above to choose its months.</p>
                  )}
                  {sel.transport?.assigned ? (
                    <div>
                      <Label className="flex items-center gap-2">Transport Month(s) <span className="text-xs text-slate-400">({fmt(sel.transport.fee)}/mo)</span>{transportDiscounted && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 text-[10px] font-semibold flex items-center gap-1" data-testid="transport-discounted"><BadgePercent size={11} />Fees Discounted</span>}</Label>
                      <div className="grid grid-cols-4 gap-1.5 mt-1" data-testid="transport-month-list">
                        {sessionMonths(sel.student.academic_session).map((m) => {
                          const on = selectedTMonths.includes(m);
                          const paid = paidTMonths.has(m);
                          const allowed = !sel.transport.months.length || sel.transport.months.includes(m);
                          const disabled = paid || !allowed || transportDiscounted;
                          return (
                            <button key={m} type="button" disabled={disabled} onClick={() => toggleTMonth(m)} title={transportDiscounted ? "Fees discounted" : paid ? "Already paid" : (allowed ? "" : "Not assigned in transport")} className={`px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${transportDiscounted ? "bg-amber-50 text-amber-400 border-amber-200 cursor-not-allowed" : paid ? "bg-emerald-50 text-emerald-600 border-emerald-200 cursor-not-allowed line-through" : !allowed ? "bg-rose-50 text-rose-400 border-rose-200 cursor-not-allowed line-through" : on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`} data-testid={`tmonth-${m.replace(" ", "-")}`}>{m}{paid ? " ✓" : ""}</button>
                          );
                        })}
                      </div>
                      {transportDiscounted && <p className="text-xs text-amber-600 mt-1">Transport fee is discounted on the Edit Fees page and can't be collected here.</p>}
                      {sel.transport.months.length > 0 && <p className="text-xs text-slate-400 mt-1">Red months aren't assigned in transport and can't be collected.</p>}
                      <p className="text-xs text-slate-400 mt-1">Months with ✓ are already paid for transport.</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <span className="text-xs text-amber-700">No transport assigned</span>
                      <Button type="button" size="sm" variant="outline" className="text-indigo-600" onClick={openAssignT} data-testid="assign-transport-btn"><Bus size={14} className="mr-1" />Assign Transport</Button>
                    </div>
                  )}
                </div>

                <div className="space-y-3 lg:border-l lg:border-slate-100 lg:pl-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Receipt No.</Label>
                      <Input value={receiptNo} onChange={(e) => setReceiptNo(e.target.value)} className="font-mono" data-testid="receipt-no-input" />
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input type="datetime-local" value={paymentDate} disabled={!allowEditDate} onChange={(e) => setPaymentDate(e.target.value)} data-testid="payment-date-input" />
                    </div>
                  </div>
                  {!allowEditDate && <p className="text-xs text-slate-400 -mt-1">Date editing is disabled in Fee Settings.</p>}
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Payment Mode</Label>
                      <Select value={mode} onValueChange={setMode}><SelectTrigger data-testid="mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{["Cash", "UPI", "Bank Transfer", "Cheque", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div><Label>Remarks</Label><Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="remarks-input" /></div>
                  </div>
                  <div className="flex items-center gap-2 pt-2" data-testid="manual-amount-toggle-row">
                    <input id="manualMode" type="checkbox" checked={manualMode} onChange={(e) => { setManualMode(e.target.checked); setManualAmount(""); setOldDuesAlloc([]); if (!e.target.checked) { setSelectedHeads(new Set()); setHeadMonths({}); setSelectedTMonths([]); setAmount(""); } }} className="accent-indigo-600 w-4 h-4" data-testid="manual-amount-toggle" />
                    <label htmlFor="manualMode" className="text-sm text-slate-700 font-medium">Enter Amount Manually (auto-distribute)</label>
                  </div>
                  {manualMode && (
                    <div data-testid="manual-amount-box">
                      <Label>Total Amount to Distribute</Label>
                      <Input type="number" value={manualAmount} onChange={(e) => { setManualAmount(e.target.value); distributeManual(e.target.value); }} placeholder="e.g. 5000" data-testid="manual-amount-input" />
                      <p className="text-xs text-slate-400 mt-1">Clears old dues first, then month by month April → March across all heads.</p>
                      {oldDuesAlloc.length > 0 && (
                        <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded-lg p-2 space-y-0.5" data-testid="manual-olddues-alloc">
                          {oldDuesAlloc.map((o) => <div key={o.id} className="flex justify-between"><span className="text-amber-700">{o.label} (Old Dues)</span><span className="tabular font-semibold">{fmt(o.amount)}</span></div>)}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="pt-3 border-t border-slate-200">
                    <Label>{manualMode ? "Current Session Amount (auto)" : "Amount Being Paid"}</Label>
                    <Input ref={amtRef} type="number" max={sel.fees.due} className="h-12 text-xl font-bold tabular" value={amount} onChange={(e) => { const raw = e.target.value; if (raw === "") return setAmount(""); setAmount(String(Math.min(Number(raw), sel.fees.due))); }} data-testid="amount-input" />
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-slate-400">Auto-calculated · max {fmt(sel.fees.due)}</span>
                      <button type="button" onClick={selectFullDue} className="text-xs text-indigo-600 hover:underline" data-testid="pay-full-due">Full Due</button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-sm bg-slate-50 rounded-lg px-3 h-12">
                    <span className="text-slate-500">Remaining Due after payment</span>
                    <span className={`font-bold tabular ${remaining > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(remaining)}</span>
                  </div>
                  <Button className="w-full h-12 text-base" onClick={save} disabled={saving} data-testid="save-payment-btn">
                    {saving ? <Loader2 className="animate-spin" /> : <><HandCoins size={18} className="mr-2" />Save & Generate Receipt</>}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {receipt && (
          <div className="p-10 text-center bg-white rounded-xl border border-slate-200 max-w-lg mx-auto mt-6" data-testid="receipt-success">
            <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3" />
            <h3 className="font-heading text-xl font-bold text-slate-900">Payment Saved</h3>
            <p className="text-slate-500 mt-1">Receipt <span className="font-mono font-semibold text-slate-700">{receipt.receipt_number}</span></p>
            <div className="text-3xl font-heading font-extrabold text-slate-900 my-4 tabular">{fmt(receipt.amount_paid)}</div>
            <div className="flex gap-2 justify-center">
              <a href={`${API}/receipts/${receipt.id}/pdf?token=${token}`} target="_blank" rel="noreferrer">
                <Button variant="outline" data-testid="print-receipt-btn"><Printer size={16} className="mr-2" />Print / Download</Button>
              </a>
              {studentId
                ? <Button onClick={() => onOpenChange(false)} data-testid="done-btn">Done</Button>
                : <Button onClick={reset} data-testid="new-payment-btn">New Payment</Button>}
            </div>
          </div>
        )}
      </div>
    </div>

      <CollectOldDuesDialog open={oldDuesOpen} onOpenChange={setOldDuesOpen} studentId={sel?.student?.id}
        onCollected={() => { if (sel) loadPreview(sel.student.id); onCollected?.(); }} />

      <Dialog open={assignTOpen} onOpenChange={setAssignTOpen}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col p-0 gap-0" data-testid="assign-transport-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0"><DialogTitle>Add/Edit Transport{sel ? ` — ${sel.student.name}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3 overflow-y-auto px-6 flex-1 pb-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Route</Label>
                <Select value={tRoute} onValueChange={(v) => { const r = routes.find((x) => x.id === v); setTRoute(v); setTFee(r?.monthly_fee || 0); if (!(r?.pickup_points || []).includes(tPickup)) setTPickup(""); }}>
                  <SelectTrigger data-testid="t-route"><SelectValue placeholder="Select route" /></SelectTrigger>
                  <SelectContent>{routes.map((r) => <SelectItem key={r.id} value={r.id}>{r.route_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Vehicle</Label><Select value={tVehicle} onValueChange={setTVehicle}><SelectTrigger data-testid="t-vehicle"><SelectValue placeholder="Select vehicle" /></SelectTrigger><SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.vehicle_name || v.vehicle_number}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Pickup Point</Label>
                {(() => { const pk = (routes.find((r) => r.id === tRoute)?.pickup_points) || []; return (
                  <Select value={tPickup} onValueChange={setTPickup} disabled={!tRoute || pk.length === 0}>
                    <SelectTrigger data-testid="t-pickup"><SelectValue placeholder={!tRoute ? "Select a route first" : (pk.length ? "Select pickup point" : "No pickup points for this route")} /></SelectTrigger>
                    <SelectContent>{pk.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                ); })()}
              </div>
              <div><Label>Transport Fee (monthly)</Label><Input type="number" value={tFee} onChange={(e) => setTFee(e.target.value)} data-testid="t-fee" /></div>
            </div>
            <div>
              <Label>Transport Months <span className="text-xs text-slate-400">({tAssignMonths.length || "all 12"})</span></Label>
              <label className="flex items-center gap-2 mt-1 mb-1.5 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" className="accent-indigo-600 w-4 h-4" checked={tAssignMonths.length === sessionMonths(sel?.student?.academic_session).length} onChange={(e) => setTAssignMonths(e.target.checked ? sessionMonths(sel?.student?.academic_session) : [])} data-testid="t-select-all-months" />
                Select All Months
              </label>
              <div className="grid grid-cols-4 gap-1.5 mt-1">
                {sessionMonths(sel?.student?.academic_session).map((m) => {
                  const on = tAssignMonths.includes(m);
                  return <button key={m} type="button" onClick={() => setTAssignMonths(on ? tAssignMonths.filter((x) => x !== m) : [...tAssignMonths, m])} className={`px-2 py-1.5 rounded-md text-xs font-medium border ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`t-assign-month-${m.replace(" ", "-")}`}>{m}</button>;
                })}
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t shrink-0"><Button onClick={saveAssignT} data-testid="save-assign-transport">Save Transport</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
